import { FC, useState } from "react";
import { Box, styled } from "@mui/system";
import { GullButton } from "../buttons/GullButton";
import { tableHeadData, statementData } from "./statement-table-data";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 600,
  "& th, td": {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  "& th": {
    color: theme.palette.grey.main,
    "& .MuiTableSortLabel-icon": {
      color: theme.palette.grey[700],
    },
    "& :hover": {
      color: theme.palette.grey[600],
    },
    "& .Mui-active": {
      color: theme.palette.grey[700],
      "& .MuiTableSortLabel-icon": {
        color: theme.palette.grey[700],
      },
    },
  },
  "& td": {
    paddingTop: ".7rem",
    paddingBottom: ".7rem",
  },
  "& #TableBodyRow": {
    "&:last-child td, &:last-child th": {
      borderBottom: theme.palette.grey[300],
    },
  },
}));

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface Data {
  date: string;
  order_id: number;
  details: string;
  amount: number;
}
const StatementTable: FC = () => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Data>("date");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  return (
    <Box p="1rem">
      <StyledTable>
        <TableHead>
          <TableRow>
            {tableHeadData.map((headCell) => (
              <TableCell
                key={headCell.id}
                align={"left"}
                padding={headCell.disablePadding ? "none" : "normal"}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort(statementData, getComparator(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => {
              return (
                <TableRow tabIndex={-1} key={row.id} id="TableBodyRow">
                  <TableCell padding="none" align="left">
                    {row.date}
                  </TableCell>
                  <TableCell align="left">{row.order_id}</TableCell>
                  <TableCell align="left">{row.details}</TableCell>
                  <TableCell align="left">{`$${row.amount}.00`}</TableCell>
                  <TableCell padding="none" align="center">
                    <GullButton
                      variant="outlined"
                      color="primary"
                      sx={{ p: ".2rem .8rem", fontSize: 12 }}
                    >
                      Download
                    </GullButton>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </StyledTable>
      <TablePagination
        page={page}
        component="div"
        rowsPerPage={rowsPerPage}
        count={statementData.length}
        onPageChange={handleChangePage}
        rowsPerPageOptions={[5, 10, 20]}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default StatementTable;
